// ─────────────────────────────────────────────────────────────────────────────
// CORE BLOCKS v2 — design-system-driven rendering for the blocks the AI uses most.
//
// Why this exists: the legacy renderers in blockHtml.ts hard-code their look
// (white background, #666 text, 44px/800 headings, 8px radius, #5b6af0 buttons).
// Whatever design system the AI invented, the page came out the same generic
// template. These renderers draw everything from the site design tokens
// (--t-* from theme.ts): palette, heading/body fonts, type scale, radii,
// shadows, button shape — with editorial layouts, eyebrows, display type and
// art-directed placeholders when a photo is missing.
//
// Enabled per site with themeConfig.designSystem === 'v2' (set when an AI
// generation is applied). Sites without it keep the legacy look untouched.
// Every renderer returns null for types it doesn't handle → legacy fallback.
//
// Data contract: each renderer reads the canonical defaultData field names AND
// the aliases the AI uses (see apps/api/src/ai/generation/harmonize-blocks.ts).
// Optional v2 fields: eyebrow, tone ("alt" | "dark" | "accent").
// ─────────────────────────────────────────────────────────────────────────────

type D = Record<string, unknown>
interface CoreBlock { id: string; type: string; data: D }

const e = (s: unknown): string => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const u = (url: unknown): string => {
  const s = String(url ?? '').trim()
  return e(/^\s*(javascript|data|vbscript):/i.test(s) || !s ? '#' : s)
}
const css = (v: unknown): string => String(v ?? '').replace(/["'<>;{}]/g, '').trim()
const pick = (d: D, ...keys: string[]): unknown => {
  for (const k of keys) { const v = d[k]; if (v !== undefined && v !== null && v !== '') return v }
  return undefined
}
const str = (d: D, ...keys: string[]): string => {
  const v = pick(d, ...keys)
  if (v && typeof v === 'object' && !Array.isArray(v)) return String((v as D).text ?? '')
  return v === undefined ? '' : String(v)
}
const list = <T = D>(d: D, ...keys: string[]): T[] => {
  for (const k of keys) if (Array.isArray(d[k]) && (d[k] as unknown[]).length) return d[k] as T[]
  return []
}
const btnUrl = (d: D, ...keys: string[]): string => {
  const v = pick(d, ...keys)
  if (v && typeof v === 'object') return String((v as D).url ?? '#')
  return v === undefined ? '#' : String(v)
}

function lum(hex: string): number | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  let h = m[1]; if (h.length === 3) h = h.split('').map(c => c + c).join('')
  return (parseInt(h.slice(0, 2), 16) * 299 + parseInt(h.slice(2, 4), 16) * 587 + parseInt(h.slice(4, 6), 16) * 114) / 1000
}

/** Section wrapper: tone + explicit background from data. */
function section(d: D, inner: string, extraClass = '', extraStyle = ''): string {
  const tone = String(d.tone ?? '')
  const cls = ['tb', extraClass, tone === 'alt' ? 'tb-alt' : '', tone === 'dark' ? 'tb-dark' : '', tone === 'accent' ? 'tb-accent' : ''].filter(Boolean)
  const styles: string[] = []
  const bg = css(pick(d, 'background', 'bgColor', 'bg'))
  if (bg && !tone && !/^(#fff|#ffffff|white|transparent)$/i.test(bg)) {
    styles.push(`--_secbg:${bg}`)
    const l = lum(bg)
    if (l !== null && l < 110) cls.push('tb-on-dark')
  }
  const accent = css(d.accentColor)
  if (accent && /^#[0-9a-f]{3,6}$/i.test(accent) && accent.toLowerCase() !== '#5b6af0') {
    styles.push(`--_ac:${accent}`)
    const l = lum(accent); if (l !== null) styles.push(`--_on:${l > 150 ? '#111' : '#fff'}`)
  }
  if (extraStyle) styles.push(extraStyle)
  return `<div class="${cls.join(' ')}"${styles.length ? ` style="${styles.join(';')}"` : ''}>${inner}</div>`
}

function eyebrow(d: D, fallback = ''): string {
  const t = str(d, 'eyebrow', 'badge', 'label', 'tag') || fallback
  return t ? `<div class="tb-eyebrow">${e(t)}</div>` : ''
}

function buttons(d: D, opts: { center?: boolean } = {}): string {
  const p = str(d, 'buttonText', 'primaryCta', 'ctaText', 'primaryButtonText', 'primaryButton')
  const pUrl = btnUrl(d, 'buttonUrl', 'primaryCtaUrl', 'ctaUrl', 'primaryButtonUrl', 'primaryUrl', 'primaryButton')
  const s = str(d, 'secondaryCta', 'secondaryText', 'secondaryButtonText', 'secondaryButton')
  const sUrl = btnUrl(d, 'secondaryCtaUrl', 'secondaryUrl', 'secondaryButtonUrl', 'secondaryButton')
  if (!p && !s) return ''
  return `<div class="tb-actions${opts.center ? ' center' : ''}">${p ? `<a class="tb-btn" href="${u(pUrl)}">${e(p)}<span class="tb-arr" aria-hidden="true">→</span></a>` : ''}${s ? `<a class="tb-btn ghost" href="${u(sUrl)}">${e(s)}</a>` : ''}</div>`
}

/** Photo or an art-directed placeholder (gradient + monogram) — never an empty grey box. */
function media(src: unknown, alt: unknown, label: string, cls = '', caption = ''): string {
  const s = String(src ?? '').trim()
  if (s) return `<div class="tb-media ${cls}"><img src="${u(s)}" alt="${e(alt ?? label)}" loading="lazy" decoding="async"></div>`
  const mono = (label.trim().match(/[\p{L}\p{N}]/u)?.[0] ?? '✦').toUpperCase()
  return `<div class="tb-media tb-ph ${cls}" role="img" aria-label="${e(label)}"><span class="tb-ph-mono">${e(mono)}</span>${caption ? `<span class="tb-ph-cap">${e(caption)}</span>` : ''}</div>`
}

function head(d: D, opts: { center?: boolean; fallbackEyebrow?: string } = {}): string {
  const title = str(d, 'title', 'heading')
  const sub = str(d, 'subtitle', 'description', 'text')
  if (!title && !sub) return ''
  return `<div class="tb-head${opts.center ? ' center' : ''}"><div>${eyebrow(d, opts.fallbackEyebrow)}${title ? `<h2 class="tb-h2">${e(title)}</h2>` : ''}</div>${sub ? `<p class="tb-lead">${e(sub)}</p>` : ''}</div>`
}

const pad2 = (i: number) => String(i + 1).padStart(2, '0')
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '•'
const isEmoji = (s: string) => /\p{Extended_Pictographic}/u.test(s)

function icon(raw: unknown, i: number): string {
  const s = String(raw ?? '').trim()
  // Emoji icons look cheap next to display type; use a numbered mark instead,
  // keep short glyphs (✦ ◆ №) and plain text symbols.
  if (!s || isEmoji(s) || s.length > 3) return `<span class="tb-ic">${pad2(i)}</span>`
  return `<span class="tb-ic">${e(s)}</span>`
}

// ── Renderers ────────────────────────────────────────────────────────────────

function navbar(d: D): string {
  const links = list<D>(d, 'links', 'items')
  const logo = str(d, 'logo', 'brand', 'logoText') || 'Brand'
  const logoImg = String(d.logoImage ?? d.logoUrl ?? '')
  const cta = str(d, 'ctaText', 'buttonText')
  const nav = links.map(l => `<a href="${u(l.url ?? l.href)}">${e(l.label ?? l.text)}</a>`).join('')
  return `<header class="tb-nav${d.sticky === false ? '' : ' sticky'}"><div class="tb-nav-in"><a class="tb-logo" href="#">${logoImg ? `<img src="${u(logoImg)}" alt="${e(logo)}">` : e(logo)}</a><nav class="tb-links">${nav}</nav>${cta ? `<a class="tb-btn sm" href="${u(btnUrl(d, 'ctaUrl', 'buttonUrl'))}">${e(cta)}</a>` : ''}<details class="tb-burger"><summary aria-label="Меню"><span></span><span></span></summary><div class="tb-drawer">${nav}${cta ? `<a class="tb-btn" href="${u(btnUrl(d, 'ctaUrl', 'buttonUrl'))}">${e(cta)}</a>` : ''}</div></details></div></header>`
}

function heroCentered(d: D): string {
  const bgImg = String(pick(d, 'backgroundImage', 'image', 'imageUrl') ?? '')
  const title = str(d, 'title', 'heading')
  const stats = list<D>(d, 'stats', 'metrics')
  const statsHtml = stats.length ? `<div class="tb-hero-stats">${stats.slice(0, 4).map(s => `<div><b>${e(s.value)}</b><span>${e(s.label)}</span></div>`).join('')}</div>` : ''
  const inner = `<div class="tb-in tb-hero-c">${eyebrow(d)}<h1 class="tb-h1">${e(title)}</h1>${str(d, 'subtitle') ? `<p class="tb-lead">${e(str(d, 'subtitle'))}</p>` : ''}${buttons(d, { center: true })}${statsHtml}</div>`
  if (bgImg) {
    return section({ ...d, background: undefined, tone: undefined }, `<div class="tb-hero-bg"><img src="${u(bgImg)}" alt="" loading="eager"></div>${inner}`, 'tb-hero tb-hero-photo tb-on-dark')
  }
  return section(d, `<div class="tb-glow" aria-hidden="true"></div><div class="tb-gridlines" aria-hidden="true"></div>${inner}`, 'tb-hero')
}

function heroSplit(d: D): string {
  const title = str(d, 'title', 'heading', 'leftTitle')
  const sub = str(d, 'subtitle', 'description', 'leftText')
  const img = pick(d, 'image', 'imageUrl', 'photo', 'rightImage', 'screenshot')
  const pos = String(pick(d, 'imagePosition', 'photoPosition') ?? (d.textSide === 'right' ? 'left' : 'right'))
  const feats = list<unknown>(d, 'features').map(f => typeof f === 'string' ? f : String((f as D).text ?? (f as D).value ?? (f as D).title ?? '')).filter(Boolean)
  const stats = list<D>(d, 'stats', 'metrics')
  const chips = feats.length ? `<ul class="tb-checks">${feats.slice(0, 4).map(f => `<li>${e(f)}</li>`).join('')}</ul>` : ''
  const floatCard = stats[0] ? `<div class="tb-float tb-float-a"><b>${e(stats[0].value)}</b><span>${e(stats[0].label)}</span></div>` : ''
  const floatCard2 = stats[1] ? `<div class="tb-float tb-float-b"><b>${e(stats[1].value)}</b><span>${e(stats[1].label)}</span></div>` : ''
  const text = `<div class="tb-split-text">${eyebrow(d)}<h1 class="tb-h1">${e(title)}</h1>${sub ? `<p class="tb-lead">${e(sub)}</p>` : ''}${buttons(d)}${chips}</div>`
  const vis = `<div class="tb-split-media">${media(img, d.imageAlt, title, 'tall')}${floatCard}${floatCard2}</div>`
  return section(d, `<div class="tb-glow${pos === 'left' ? ' left' : ''}" aria-hidden="true"></div><div class="tb-in tb-split${pos === 'left' ? ' rev' : ''}">${text}${vis}</div>`, 'tb-hero')
}

function heroMinimal(d: D): string {
  const title = str(d, 'title', 'heading')
  const sub = str(d, 'subtitle')
  const align = String(d.align ?? 'left')
  return section(d, `<div class="tb-in tb-hero-min${align === 'center' ? ' center' : ''}">${eyebrow(d)}<h1 class="tb-h1 xl">${e(title)}</h1><div class="tb-hero-min-row">${sub ? `<p class="tb-lead">${e(sub)}</p>` : '<span></span>'}${buttons(d)}</div></div>`, 'tb-hero')
}

function stats(d: D): string {
  const items = list<D>(d, 'items', 'stats', 'metrics')
  if (!items.length) return ''
  const title = str(d, 'title')
  return section({ tone: d.tone ?? 'alt', ...d }, `<div class="tb-in">${title ? `<div class="tb-head"><div>${eyebrow(d)}<h2 class="tb-h2">${e(title)}</h2></div></div>` : ''}<div class="tb-stats" style="--n:${Math.min(items.length, 4)}">${items.slice(0, 8).map(it => `<div class="tb-stat"><b>${e(`${it.prefix ?? ''}${it.value ?? ''}${it.suffix ?? ''}`)}</b><span>${e(it.label ?? it.description)}</span></div>`).join('')}</div></div>`, 'tb-tight')
}

function features(d: D): string {
  const items = list<D>(d, 'items', 'features', 'cards')
  const cols = Math.max(2, Math.min(Number(d.columns ?? (items.length % 3 === 0 ? 3 : items.length === 4 ? 4 : 3)) || 3, 4))
  const cards = items.map((it, i) => `<article class="tb-card tb-feat">${icon(it.icon, i)}<h3 class="tb-h3">${e(it.title)}</h3><p>${e(it.description ?? it.text)}</p></article>`).join('')
  return section(d, `<div class="tb-in">${head(d)}<div class="tb-grid" style="--cols:${cols}">${cards}</div></div>`)
}

function alternating(d: D): string {
  const items = list<D>(d, 'items', 'features')
  const rows = items.map((it, i) => `<div class="tb-alt-row${i % 2 ? ' rev' : ''}">${media(it.image ?? it.imageUrl, it.title, String(it.title ?? ''), 'wide')}<div class="tb-alt-text">${it.tag ? `<div class="tb-eyebrow">${e(it.tag)}</div>` : `<div class="tb-num">${pad2(i)}</div>`}<h3 class="tb-h2 sm">${e(it.title)}</h3><p class="tb-lead">${e(it.text ?? it.description)}</p>${it.buttonText ? `<a class="tb-link" href="${u(it.buttonUrl)}">${e(it.buttonText)} →</a>` : ''}</div></div>`).join('')
  return section(d, `<div class="tb-in">${head(d)}<div class="tb-alts">${rows}</div></div>`)
}

function imageText(d: D): string {
  const title = str(d, 'title', 'heading')
  const pos = String(pick(d, 'imagePosition') ?? 'left')
  const text = `<div class="tb-split-text">${eyebrow(d)}<h2 class="tb-h2">${e(title)}</h2><p class="tb-lead">${e(str(d, 'text', 'description', 'subtitle'))}</p>${buttons(d)}</div>`
  return section(d, `<div class="tb-in tb-split even${pos === 'right' ? '' : ' rev'}">${text}${media(pick(d, 'image', 'imageUrl'), d.imageAlt, title, 'tall')}</div>`)
}

function steps(d: D): string {
  const items = list<D>(d, 'items', 'steps')
  const cells = items.map((it, i) => `<li class="tb-step"><span class="tb-step-n">${e(it.number ?? it.year ?? it.date ?? pad2(i))}</span><h3 class="tb-h3">${e(it.title)}</h3><p>${e(it.description ?? it.text)}</p></li>`).join('')
  return section(d, `<div class="tb-in">${head(d)}<ol class="tb-steps" style="--cols:${Math.min(Math.max(items.length, 2), 4)}">${cells}</ol></div>`)
}

function stars(n: unknown): string {
  const r = Math.max(0, Math.min(5, Math.round(Number(n ?? 5)) || 0))
  return r ? `<div class="tb-stars" aria-label="${r} из 5">${'★'.repeat(r)}</div>` : ''
}

function testimonials(d: D): string {
  const items = list<D>(d, 'items', 'testimonials', 'reviews')
  if (!items.length) return null as unknown as string
  const [first, ...rest] = items
  const big = `<figure class="tb-quote-big"><blockquote>«${e(first.text ?? first.quote)}»</blockquote><figcaption><span class="tb-ava">${e(initials(String(first.name ?? '')))}</span><span><b>${e(first.name)}</b>${[first.role, first.company, first.source].filter(Boolean).length ? `<i>${e([first.role, first.company, first.source].filter(Boolean).join(' · '))}</i>` : ''}</span></figcaption></figure>`
  const cards = rest.map(it => `<figure class="tb-card tb-review">${stars(it.rating)}<blockquote>${e(it.text ?? it.quote)}</blockquote><figcaption><span class="tb-ava sm">${e(initials(String(it.name ?? '')))}</span><span><b>${e(it.name)}</b>${it.role || it.company ? `<i>${e([it.role, it.company].filter(Boolean).join(' · '))}</i>` : ''}</span></figcaption></figure>`).join('')
  return section(d, `<div class="tb-in">${head(d)}<div class="tb-reviews${rest.length ? '' : ' solo'}">${big}${rest.length ? `<div class="tb-review-col">${cards}</div>` : ''}</div></div>`)
}

function testimonialSingle(d: D): string {
  const name = str(d, 'name', 'author')
  return section(d, `<div class="tb-in"><figure class="tb-quote-big center">${stars(d.rating)}<blockquote>«${e(str(d, 'quote', 'text'))}»</blockquote><figcaption><span class="tb-ava">${e(initials(name))}</span><span><b>${e(name)}</b>${d.role || d.company ? `<i>${e([d.role, d.company].filter(Boolean).join(' · '))}</i>` : ''}</span></figcaption></figure></div>`)
}

function team(d: D): string {
  const items = list<D>(d, 'items', 'members')
  const cols = Math.min(Math.max(items.length, 2), 4)
  const cards = items.map(m => `<article class="tb-member">${media(m.photo ?? m.image ?? m.avatar, m.name, String(m.name ?? ''), 'portrait')}<h3 class="tb-h3">${e(m.name)}</h3><span class="tb-role">${e(m.role)}</span>${m.bio ? `<p>${e(m.bio)}</p>` : ''}</article>`).join('')
  return section(d, `<div class="tb-in">${head(d)}<div class="tb-grid" style="--cols:${cols}">${cards}</div></div>`)
}

function cardGrid(d: D): string {
  const items = list<D>(d, 'items', 'cards', 'products')
  const cols = Math.max(2, Math.min(Number(d.columns ?? 3) || 3, 4))
  const cards = items.map(it => {
    const title = String(it.title ?? it.name ?? '')
    const link = it.link ?? it.url ?? it.buttonUrl
    const price = it.price ? `<div class="tb-price-row"><b>${e(it.price)}</b>${it.oldPrice ? `<s>${e(it.oldPrice)}</s>` : ''}</div>` : ''
    return `<article class="tb-card tb-media-card">${it.badge ? `<span class="tb-pill">${e(it.badge)}</span>` : ''}${media(it.image ?? it.imageUrl, title, title, 'card')}<div class="tb-mc-body"><h3 class="tb-h3">${e(title)}</h3>${it.text || it.description ? `<p>${e(it.text ?? it.description)}</p>` : ''}${price}${link || it.linkText || it.buttonText ? `<a class="tb-link" href="${u(link)}">${e(it.linkText ?? it.buttonText ?? 'Подробнее')} →</a>` : ''}</div></article>`
  }).join('')
  return section(d, `<div class="tb-in">${head(d)}<div class="tb-grid" style="--cols:${cols}">${cards}</div></div>`)
}

function gallery(d: D): string {
  const imgs = list<D>(d, 'images', 'items')
  const title = str(d, 'title')
  const cells = imgs.slice(0, 9).map((im, i) => `<div class="tb-g${i === 0 ? ' big' : ''}">${media(im.url ?? im.src ?? im.image, im.alt ?? im.caption, String(im.caption ?? im.alt ?? title ?? ''), '', String(im.caption ?? ''))}</div>`).join('')
  return section(d, `<div class="tb-in">${head(d)}<div class="tb-gallery">${cells}</div></div>`)
}

function pricing(d: D): string {
  const plans = list<D>(d, 'plans', 'items')
  const cols = Math.min(Math.max(plans.length, 1), 4)
  const cards = plans.map(p => {
    const hi = !!(p.highlighted ?? p.popular)
    const price = String(p.price ?? p.monthlyPrice ?? '')
    const feats = (Array.isArray(p.features) ? p.features : []) as unknown[]
    return `<article class="tb-plan${hi ? ' hi' : ''}">${hi ? `<span class="tb-pill">${e(p.badge ?? 'Популярно')}</span>` : ''}<h3 class="tb-h3">${e(p.name)}</h3>${p.description ? `<p class="tb-plan-d">${e(p.description)}</p>` : ''}<div class="tb-plan-price"><b>${e(price)}</b>${p.period ? `<span>/ ${e(p.period)}</span>` : ''}</div><ul class="tb-checks">${feats.map(f => `<li>${e(typeof f === 'string' ? f : (f as D).text ?? (f as D).value)}</li>`).join('')}</ul><a class="tb-btn${hi ? '' : ' ghost'} full" href="${u(p.ctaUrl ?? p.buttonUrl ?? '#')}">${e(p.ctaText ?? p.buttonText ?? p.cta ?? 'Выбрать')}</a></article>`
  }).join('')
  return section(d, `<div class="tb-in">${head(d, { center: true })}<div class="tb-grid tb-plans" style="--cols:${cols}">${cards}</div></div>`)
}

function faq(d: D): string {
  const items = list<D>(d, 'items', 'questions')
  const qa = items.map((it, i) => `<details class="tb-qa"${i === 0 ? ' open' : ''}><summary><span>${e(it.question ?? it.title)}</span><i aria-hidden="true"></i></summary><p>${e(it.answer ?? it.content ?? it.text)}</p></details>`).join('')
  const title = str(d, 'title')
  return section(d, `<div class="tb-in tb-faq"><div>${eyebrow(d, 'FAQ')}<h2 class="tb-h2">${e(title || 'Вопросы и ответы')}</h2>${str(d, 'subtitle') ? `<p class="tb-lead">${e(str(d, 'subtitle'))}</p>` : ''}</div><div class="tb-qas">${qa}</div></div>`)
}

function cta(d: D): string {
  const title = str(d, 'title', 'heading', 'text')
  const variant = String(d.variant ?? 'accent')
  const panelCls = variant === 'light' ? 'tb-panel soft' : variant === 'dark' ? 'tb-panel dark' : 'tb-panel'
  const gradient = d.gradientFrom && d.gradientTo ? ` style="background:linear-gradient(135deg,${css(d.gradientFrom)},${css(d.gradientTo)})"` : ''
  return section({ ...d, background: undefined }, `<div class="tb-in"><div class="${panelCls}"${gradient}><div class="tb-glow" aria-hidden="true"></div>${eyebrow(d)}<h2 class="tb-h2">${e(title)}</h2>${str(d, 'subtitle') ? `<p class="tb-lead">${e(str(d, 'subtitle'))}</p>` : ''}${buttons(d, { center: true })}</div></div>`, 'tb-tight')
}

function quote(d: D): string {
  const roleStr = [d.author, d.role].filter(Boolean).map(String).join(', ')
  return section(d, `<div class="tb-in"><figure class="tb-quote-big center"><blockquote>«${e(str(d, 'text', 'quote'))}»</blockquote>${roleStr ? `<figcaption><span><b>${e(roleStr)}</b></span></figcaption>` : ''}</figure></div>`)
}

function logoStrip(d: D): string {
  const items = list<D>(d, 'items', 'logos')
  if (!items.length) return null as unknown as string
  return section({ tone: 'alt', ...d }, `<div class="tb-in tb-logos">${str(d, 'title') ? `<p class="tb-logos-t">${e(str(d, 'title'))}</p>` : ''}<div class="tb-logos-row">${items.map(it => it.logo || it.image ? `<img src="${u(it.logo ?? it.image)}" alt="${e(it.name)}">` : `<span>${e(it.name ?? it.text)}</span>`).join('')}</div></div>`, 'tb-tight')
}

function trustBar(d: D): string {
  const items = list<D>(d, 'items')
  if (!items.length) return null as unknown as string
  return section({ tone: 'alt', ...d }, `<div class="tb-in tb-trust">${items.map((it, i) => `<div>${icon(it.icon, i)}<b>${e(it.title ?? it.text)}</b>${it.title && it.text ? `<span>${e(it.text)}</span>` : ''}</div>`).join('')}</div>`, 'tb-tight')
}

function marquee(d: D): string {
  const items = list<D | string>(d, 'items')
  if (!items.length) return null as unknown as string
  const words = items.map(it => typeof it === 'string' ? it : String(it.text ?? it.label ?? ''))
  const run = words.map(w => `<span>${e(w)}</span><i>✦</i>`).join('')
  return `<div class="tb tb-marquee" aria-hidden="false"><div class="tb-mq-track">${run}${run}${run}</div></div>`
}

function footer(d: D): string {
  const links = list<D>(d, 'links')
  const cols = list<D>(d, 'columns')
  const social = list<D>(d, 'socialLinks', 'social')
  const brand = str(d, 'brand', 'logo', 'companyName') || ''
  const colHtml = cols.length
    ? cols.map(c => `<div class="tb-fcol"><h4>${e(c.title)}</h4>${(Array.isArray(c.links) ? c.links as D[] : []).map(l => `<a href="${u(l.url)}">${e(l.label)}</a>`).join('')}</div>`).join('')
    : (links.length ? `<div class="tb-fcol"><h4>Навигация</h4>${links.map(l => `<a href="${u(l.url)}">${e(l.label)}</a>`).join('')}</div>` : '')
  const socialHtml = social.length ? `<div class="tb-fcol"><h4>Соцсети</h4>${social.map(s => `<a href="${u(s.url)}">${e(s.label ?? s.platform)}</a>`).join('')}</div>` : ''
  const contacts = [d.phone, d.email, d.address].filter(Boolean) as string[]
  const contactHtml = contacts.length ? `<div class="tb-fcol"><h4>Контакты</h4>${contacts.map(c => `<span>${e(c)}</span>`).join('')}</div>` : ''
  return `<footer class="tb tb-footer"><div class="tb-in"><div class="tb-foot-top"><div class="tb-foot-brand"><div class="tb-wordmark">${e(brand)}</div>${d.tagline ? `<p>${e(d.tagline)}</p>` : ''}</div><div class="tb-foot-cols">${colHtml}${socialHtml}${contactHtml}</div></div><div class="tb-foot-bot"><span>${e(d.copyright ?? (brand ? `© ${new Date().getFullYear()} ${brand}` : ''))}</span></div></div></footer>`
}

const RENDERERS: Record<string, (d: D, b: CoreBlock) => string | null> = {
  navbar,
  'hero-centered': heroCentered,
  'floating-badge-hero': heroCentered,
  'gradient-hero': heroCentered,
  'hero-gradient': heroCentered,
  'gradient-blob-hero': heroCentered,
  'hero-dark': (d) => heroCentered({ tone: 'dark', ...d }),
  'hero-with-stats': heroCentered,
  'hero-split': heroSplit,
  'split-hero-v2': heroSplit,
  'hero-split-v2': heroSplit,
  'photo-hero-split': heroSplit,
  'image-banner': heroSplit,
  'hero-minimal': heroMinimal,
  stats,
  'counter-anim': stats,
  features,
  'icon-features': features,
  'alternating-features': alternating,
  'image-text': imageText,
  'process-steps': steps,
  timeline: steps,
  testimonials,
  'reviews-grid': testimonials,
  'testimonial-single': testimonialSingle,
  team,
  'card-grid': cardGrid,
  'product-grid': cardGrid,
  gallery,
  'masonry-gallery': gallery,
  'lightbox-gallery': gallery,
  pricing,
  faq,
  accordion: faq,
  cta,
  'cta-gradient': cta,
  banner: cta,
  quote,
  'logo-strip': logoStrip,
  'trust-bar': trustBar,
  marquee,
  footer,
}

export const CORE_BLOCK_TYPES = new Set(Object.keys(RENDERERS))

/** v2 HTML for a block, or null to fall back to the legacy renderer. */
export function coreBlockHtml(block: CoreBlock): string | null {
  const r = RENDERERS[block.type]
  if (!r) return null
  try {
    const html = r(block.data ?? {}, block)
    return html || null
  } catch {
    return null
  }
}

// ── Stylesheet ────────────────────────────────────────────────────────────────
// Every value reads a --t-* token with a neutral fallback, so the same CSS works
// for any palette the AI picks (light, dark, warm, neon…).
export const CORE_BLOCKS_CSS = `
.tb{--_bg:var(--t-bg,#fff);--_alt:var(--t-bg-alt,color-mix(in srgb,var(--_tx) 4%,var(--_bg)));--_surf:var(--t-surface,color-mix(in srgb,var(--_tx) 2%,var(--_bg)));--_tx:var(--t-text,#141414);--_mu:var(--t-muted,color-mix(in srgb,var(--_tx) 60%,var(--_bg)));--_bd:var(--t-border,color-mix(in srgb,var(--_tx) 12%,var(--_bg)));--_ac:var(--t-accent,#141414);--_on:var(--t-on-accent,#fff);--_soft:color-mix(in srgb,var(--_ac) 14%,var(--_secbg,var(--_bg)));--_r:var(--t-radius,14px);--_rl:var(--t-radius-lg,22px);--_rx:var(--t-radius-xl,32px);--_br:var(--t-btn-radius,999px);--_sh:var(--t-shadow,0 10px 30px -12px rgba(0,0,0,.18));--_fh:var(--t-font-h,inherit);--_fb:var(--t-font-b,inherit);
position:relative;background:var(--_secbg,var(--_bg));color:var(--_tx);font-family:var(--_fb);font-size:var(--t-body,16px);line-height:1.6;padding:clamp(72px,9vw,136px) clamp(20px,5vw,64px);overflow:hidden;-webkit-font-smoothing:antialiased}
.tb *,.tb *::before,.tb *::after{box-sizing:border-box}
.tb img{display:block;max-width:100%}
.tb a{color:inherit}
.tb.tb-tight{padding-top:clamp(48px,6vw,88px);padding-bottom:clamp(48px,6vw,88px)}
.tb-alt{--_secbg:var(--_alt)}
.tb-dark,.tb-on-dark{--_tx:var(--t-paper,#F6F4F0);--_bg:var(--t-ink,#111);--_mu:color-mix(in srgb,var(--_tx) 68%,transparent);--_bd:color-mix(in srgb,var(--_tx) 16%,transparent);--_surf:color-mix(in srgb,var(--_tx) 6%,transparent);--_alt:color-mix(in srgb,var(--_tx) 4%,transparent)}
.tb-dark{--_secbg:var(--t-ink,#111)}
.tb-on-dark.tb-dark,.tb-on-dark{color-scheme:dark}
.tb-accent{--_secbg:var(--_ac);--_tx:var(--_on);--_mu:color-mix(in srgb,var(--_on) 78%,transparent);--_bd:color-mix(in srgb,var(--_on) 22%,transparent);--_ac:var(--_on);--_on:var(--t-accent,#141414)}
.tb-in{position:relative;z-index:1;max-width:1200px;margin:0 auto}
.tb-eyebrow{display:inline-flex;align-items:center;gap:12px;margin:0 0 22px;font:600 12px/1.2 var(--_fb);letter-spacing:.2em;text-transform:uppercase;color:var(--_ac)}
.tb-eyebrow::before{content:"";width:28px;height:1px;background:currentColor;opacity:.7}
.tb-h1{margin:0;font-family:var(--_fh);font-weight:var(--t-h-weight,700);font-size:clamp(40px,6.4vw,var(--t-h1,80px));line-height:var(--t-h-leading,1.02);letter-spacing:var(--t-h-tracking,-.025em);text-wrap:balance}
.tb-h1.xl{font-size:clamp(48px,9vw,128px);line-height:.95}
.tb-h2{margin:0;font-family:var(--_fh);font-weight:var(--t-h-weight,700);font-size:clamp(32px,4.6vw,var(--t-h2,56px));line-height:1.06;letter-spacing:var(--t-h-tracking,-.02em);text-wrap:balance}
.tb-h2.sm{font-size:clamp(28px,3.4vw,40px)}
.tb-h3{margin:0 0 10px;font-family:var(--_fh);font-weight:var(--t-h-weight,650);font-size:var(--t-h3,22px);line-height:1.2;letter-spacing:-.01em}
.tb-lead{margin:20px 0 0;max-width:600px;color:var(--_mu);font-size:clamp(17px,1.35vw,20px);line-height:1.6;text-wrap:pretty}
.tb p{color:var(--_mu)}
.tb-head{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:24px 64px;margin-bottom:clamp(40px,5vw,72px)}
.tb-head>div{max-width:720px}
.tb-head .tb-lead{max-width:440px;margin:0}
.tb-head.center{flex-direction:column;align-items:center;text-align:center}
.tb-head.center .tb-lead{max-width:600px}
.tb-actions{display:flex;flex-wrap:wrap;gap:12px;margin-top:36px}
.tb-actions.center{justify-content:center}
.tb-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;min-height:var(--t-btn-height,54px);padding:0 30px;border-radius:var(--_br);border:1px solid var(--_ac);background:var(--_ac);color:var(--_on)!important;font:600 15px/1 var(--_fb);letter-spacing:.01em;text-decoration:none;white-space:nowrap;cursor:pointer;transition:transform .25s cubic-bezier(.2,.8,.2,1),box-shadow .25s,background .25s,color .25s}
.tb-btn:hover{transform:translateY(-2px);box-shadow:0 14px 30px -12px color-mix(in srgb,var(--_ac) 70%,transparent)}
.tb-btn.ghost{background:transparent;color:var(--_tx)!important;border-color:var(--_bd)}
.tb-btn.ghost:hover{border-color:var(--_tx);box-shadow:none}
.tb-btn.sm{min-height:42px;padding:0 20px;font-size:14px}
.tb-btn.full{width:100%}
.tb-arr{display:inline-block;transition:transform .25s}
.tb-btn:hover .tb-arr{transform:translateX(4px)}
.tb-link{display:inline-block;margin-top:18px;font-weight:600;color:var(--_ac);text-decoration:none;border-bottom:1px solid color-mix(in srgb,var(--_ac) 35%,transparent);padding-bottom:2px}
.tb-pill{display:inline-block;padding:6px 12px;border-radius:999px;background:var(--_ac);color:var(--_on);font:600 11px/1 var(--_fb);letter-spacing:.08em;text-transform:uppercase}
.tb-card{background:var(--_surf);border:1px solid var(--_bd);border-radius:var(--_rl);padding:clamp(24px,2.6vw,36px);transition:transform .3s cubic-bezier(.2,.8,.2,1),box-shadow .3s,border-color .3s}
.tb-card:hover{transform:translateY(-4px);box-shadow:var(--_sh);border-color:color-mix(in srgb,var(--_ac) 30%,var(--_bd))}
.tb-grid{display:grid;grid-template-columns:repeat(var(--cols,3),minmax(0,1fr));gap:clamp(14px,1.6vw,24px)}
.tb-glow{position:absolute;inset:-18% -12% auto auto;width:56vw;max-width:820px;aspect-ratio:1;background:radial-gradient(closest-side,color-mix(in srgb,var(--_ac) 20%,transparent),transparent 72%);pointer-events:none}
.tb-glow.left{inset:-18% auto auto -12%}
.tb-gridlines{position:absolute;inset:0;background-image:linear-gradient(var(--_bd) 1px,transparent 1px),linear-gradient(90deg,var(--_bd) 1px,transparent 1px);background-size:72px 72px;-webkit-mask-image:radial-gradient(ellipse at 50% 30%,#000 10%,transparent 70%);mask-image:radial-gradient(ellipse at 50% 30%,#000 10%,transparent 70%);opacity:.55;pointer-events:none}
.tb-media{position:relative;overflow:hidden;border-radius:var(--_rl);background:var(--_alt)}
.tb-media img{width:100%;height:100%;object-fit:cover}
.tb-media.tall{aspect-ratio:4/5}.tb-media.wide{aspect-ratio:16/11}.tb-media.card{aspect-ratio:4/3;border-radius:calc(var(--_rl) - 6px)}.tb-media.portrait{aspect-ratio:3/4}
.tb-ph{display:grid;place-items:center;background:radial-gradient(120% 90% at 18% 12%,color-mix(in srgb,var(--_ac) 42%,transparent),transparent 58%),radial-gradient(90% 80% at 92% 100%,color-mix(in srgb,var(--_ac) 26%,transparent),transparent 60%),linear-gradient(160deg,var(--_alt),color-mix(in srgb,var(--_ac) 14%,var(--_alt)))}
.tb-ph::after{content:"";position:absolute;inset:0;background-image:repeating-linear-gradient(115deg,color-mix(in srgb,var(--_tx) 5%,transparent) 0 1px,transparent 1px 14px);mix-blend-mode:multiply;opacity:.6}
.tb-ph-mono{position:relative;z-index:1;font-family:var(--_fh);font-size:clamp(72px,12vw,200px);line-height:1;font-style:italic;color:color-mix(in srgb,var(--_ac) 48%,transparent)}
.tb-ph-cap{position:absolute;z-index:1;left:18px;bottom:16px;right:18px;font:500 11px/1.3 var(--_fb);letter-spacing:.16em;text-transform:uppercase;color:color-mix(in srgb,var(--_tx) 55%,transparent);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tb-media.card .tb-ph-mono,.tb-media.portrait .tb-ph-mono{font-size:clamp(56px,7vw,110px)}
/* nav */
.tb-nav{position:relative;z-index:50;background:color-mix(in srgb,var(--t-bg,#fff) 82%,transparent);-webkit-backdrop-filter:saturate(1.4) blur(14px);backdrop-filter:saturate(1.4) blur(14px);border-bottom:1px solid var(--t-border,rgba(0,0,0,.08));color:var(--t-text,#141414);font-family:var(--t-font-b,inherit)}
.tb-nav.sticky{position:sticky;top:0}
.tb-nav-in{max-width:1280px;margin:0 auto;height:76px;padding:0 clamp(20px,4vw,48px);display:flex;align-items:center;gap:32px}
.tb-nav .tb-btn{--_ac:var(--t-accent,#141414);--_on:var(--t-on-accent,#fff);--_br:var(--t-btn-radius,999px);--_fb:var(--t-font-b,inherit)}
.tb-logo{font-family:var(--t-font-h,inherit);font-weight:700;font-size:24px;letter-spacing:-.02em;text-decoration:none;color:inherit;margin-right:auto}
.tb-logo img{height:34px;width:auto}
.tb-links{display:flex;gap:30px}
.tb-links a{font-size:15px;text-decoration:none;color:inherit;opacity:.78;transition:opacity .2s}
.tb-links a:hover{opacity:1}
.tb-burger{display:none;position:relative}
.tb-burger summary{list-style:none;cursor:pointer;width:42px;height:42px;display:grid;place-content:center;gap:6px;border:1px solid var(--t-border,rgba(0,0,0,.12));border-radius:12px}
.tb-burger summary::-webkit-details-marker{display:none}
.tb-burger summary span{display:block;width:18px;height:1.5px;background:currentColor}
.tb-drawer{position:absolute;right:0;top:52px;min-width:240px;padding:16px;display:grid;gap:12px;background:var(--t-surface,#fff);border:1px solid var(--t-border,rgba(0,0,0,.1));border-radius:16px;box-shadow:0 20px 50px -20px rgba(0,0,0,.3)}
.tb-drawer a{text-decoration:none;color:inherit}
/* hero */
.tb-hero{padding-top:clamp(88px,11vw,168px);padding-bottom:clamp(88px,11vw,168px)}
.tb-hero-c{text-align:center;display:flex;flex-direction:column;align-items:center}
.tb-hero-c .tb-h1{max-width:1050px}
.tb-hero-c .tb-lead{margin-left:auto;margin-right:auto}
.tb-hero-photo{min-height:min(88vh,860px);display:flex;align-items:center}
.tb-hero-bg{position:absolute;inset:0}
.tb-hero-bg img{width:100%;height:100%;object-fit:cover}
.tb-hero-bg::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.35),rgba(0,0,0,.6))}
.tb-hero-photo .tb-in{width:100%}
.tb-hero-stats{display:flex;flex-wrap:wrap;justify-content:center;gap:clamp(24px,5vw,72px);margin-top:64px;padding-top:32px;border-top:1px solid var(--_bd)}
.tb-hero-stats b{display:block;font-family:var(--_fh);font-size:clamp(30px,3.4vw,44px);line-height:1}
.tb-hero-stats span{display:block;margin-top:8px;font-size:14px;color:var(--_mu)}
.tb-split{display:grid;grid-template-columns:1.08fr .92fr;gap:clamp(40px,6vw,96px);align-items:center}
.tb-split.even{grid-template-columns:1fr 1fr}
.tb-split.rev>:first-child{order:2}
.tb-split-media{position:relative}
.tb-split-media .tb-media.tall{border-radius:var(--_rx)}
.tb-float{position:absolute;display:flex;align-items:center;gap:12px;padding:14px 18px;border-radius:var(--_r);background:color-mix(in srgb,var(--_surf) 88%,transparent);-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);border:1px solid var(--_bd);box-shadow:var(--_sh);max-width:260px}
.tb-float b{font-family:var(--_fh);font-size:26px;line-height:1;color:var(--_ac);white-space:nowrap}
.tb-float span{font-size:13px;line-height:1.35;color:var(--_mu)}
.tb-float-a{left:-28px;bottom:12%}
.tb-float-b{right:-20px;top:9%}
.tb-checks{list-style:none;padding:0;margin:32px 0 0;display:grid;gap:12px}
.tb-checks li{position:relative;padding-left:30px;color:var(--_tx);font-size:15px}
.tb-checks li::before{content:"";position:absolute;left:0;top:.35em;width:18px;height:18px;border-radius:50%;background:var(--_soft) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M4 8.5l2.5 2.5L12 5.5' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/12px no-repeat}
.tb-split-text .tb-checks{display:flex;flex-wrap:wrap;gap:12px 28px}
.tb-hero-min .tb-h1{max-width:14ch}
.tb-hero-min.center{text-align:center}.tb-hero-min.center .tb-h1{margin:0 auto}
.tb-hero-min-row{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:32px;margin-top:48px;padding-top:32px;border-top:1px solid var(--_bd)}
.tb-hero-min-row .tb-lead{margin:0}.tb-hero-min-row .tb-actions{margin:0}
/* stats */
.tb-stats{display:grid;grid-template-columns:repeat(var(--n,4),minmax(0,1fr))}
.tb-stat{padding:8px clamp(16px,2.4vw,40px);border-left:1px solid var(--_bd)}
.tb-stat:first-child{border-left:0;padding-left:0}
.tb-stat b{display:block;font-family:var(--_fh);font-weight:var(--t-h-weight,600);font-size:clamp(40px,5vw,72px);line-height:1;letter-spacing:-.03em;color:var(--_tx)}
.tb-stat span{display:block;margin-top:14px;font-size:14px;color:var(--_mu);max-width:220px}
/* features */
.tb-feat p{margin:0;font-size:15px;line-height:1.65}
.tb-ic{display:inline-grid;place-items:center;min-width:48px;height:48px;padding:0 12px;margin-bottom:28px;border-radius:calc(var(--_r) - 2px);background:var(--_soft);color:var(--_ac);font:600 15px/1 var(--_fh);letter-spacing:.02em}
.tb-alts{display:grid;gap:clamp(56px,8vw,120px)}
.tb-alt-row{display:grid;grid-template-columns:1.1fr .9fr;gap:clamp(32px,6vw,96px);align-items:center}
.tb-alt-row.rev>:first-child{order:2}
.tb-num{font-family:var(--_fh);font-size:15px;color:var(--_ac);margin-bottom:18px;letter-spacing:.1em}
/* steps */
.tb-steps{list-style:none;margin:0;padding:0;display:grid;grid-template-columns:repeat(var(--cols,4),minmax(0,1fr));gap:clamp(16px,2vw,28px);counter-reset:s}
.tb-step{position:relative;padding-top:28px;border-top:1px solid var(--_bd)}
.tb-step::before{content:"";position:absolute;top:-1px;left:0;width:56px;height:2px;background:var(--_ac)}
.tb-step-n{display:block;font-family:var(--_fh);font-size:clamp(40px,4vw,56px);line-height:1;color:color-mix(in srgb,var(--_ac) 80%,var(--_tx));margin-bottom:24px}
.tb-step p{margin:0;font-size:15px}
/* reviews */
.tb-reviews{display:grid;grid-template-columns:1.15fr .85fr;gap:clamp(20px,3vw,40px);align-items:start}
.tb-reviews.solo{grid-template-columns:1fr}
.tb-review-col{display:grid;gap:clamp(14px,1.6vw,20px)}
.tb-quote-big{margin:0;padding:clamp(28px,4vw,56px);border-radius:var(--_rx);background:var(--_surf);border:1px solid var(--_bd)}
.tb-quote-big.center{max-width:920px;margin:0 auto;text-align:center;background:transparent;border:0}
.tb-quote-big blockquote{margin:0;font-family:var(--_fh);font-size:clamp(24px,2.6vw,38px);line-height:1.25;letter-spacing:-.01em;color:var(--_tx)}
.tb-quote-big figcaption,.tb-review figcaption{display:flex;align-items:center;gap:14px;margin-top:32px}
.tb-quote-big.center figcaption{justify-content:center}
.tb-review blockquote{margin:14px 0 0;color:var(--_tx);font-size:16px;line-height:1.6}
.tb-review figcaption{margin-top:22px}
.tb figcaption b{display:block;font-size:15px}
.tb figcaption i{display:block;font-style:normal;font-size:13px;color:var(--_mu)}
.tb-ava{display:inline-grid;place-items:center;flex:none;width:52px;height:52px;border-radius:50%;background:var(--_ac);color:var(--_on);font:600 16px/1 var(--_fh)}
.tb-ava.sm{width:40px;height:40px;font-size:14px;background:var(--_soft);color:var(--_ac)}
.tb-stars{color:var(--_ac);letter-spacing:3px;font-size:15px}
/* team / cards */
.tb-member .tb-media{margin-bottom:20px}
.tb-member .tb-h3{margin-bottom:4px}
.tb-role{display:block;font-size:13px;letter-spacing:.12em;text-transform:uppercase;color:var(--_ac)}
.tb-member p{margin:10px 0 0;font-size:15px}
.tb-media-card{padding:10px;position:relative}
.tb-media-card .tb-pill{position:absolute;z-index:2;top:22px;left:22px}
.tb-mc-body{padding:20px 14px 14px}
.tb-mc-body p{margin:0;font-size:15px}
.tb-price-row{display:flex;gap:10px;align-items:baseline;margin-top:14px}
.tb-price-row b{font-family:var(--_fh);font-size:22px}
.tb-price-row s{color:var(--_mu)}
/* gallery */
.tb-gallery{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));grid-auto-rows:clamp(160px,18vw,260px);gap:clamp(10px,1.2vw,16px)}
.tb-g .tb-media{height:100%}
.tb-g.big{grid-column:span 2;grid-row:span 2}
/* pricing */
.tb-plans{align-items:stretch;max-width:1120px;margin:0 auto}
.tb-plan{position:relative;display:flex;flex-direction:column;padding:clamp(28px,3vw,40px);border-radius:var(--_rx);background:var(--_surf);border:1px solid var(--_bd)}
.tb-plan .tb-pill{position:absolute;top:24px;right:24px}
.tb-plan-d{margin:4px 0 0;font-size:14px}
.tb-plan-price{display:flex;align-items:baseline;gap:8px;margin:28px 0 4px;padding-bottom:28px;border-bottom:1px solid var(--_bd)}
.tb-plan-price b{font-family:var(--_fh);font-size:clamp(36px,3.6vw,52px);line-height:1;letter-spacing:-.02em}
.tb-plan-price span{color:var(--_mu);font-size:14px}
.tb-plan .tb-checks{margin:24px 0 32px;flex:1}
.tb-plan.hi{background:var(--_tx);color:var(--_bg);border-color:var(--_tx);--_mu:color-mix(in srgb,var(--_bg) 65%,transparent);--_bd:color-mix(in srgb,var(--_bg) 16%,transparent)}
.tb-plan.hi .tb-checks li{color:var(--_bg)}
.tb-plan.hi .tb-btn{--_on:var(--t-on-accent,#fff)}
/* faq */
.tb-faq{display:grid;grid-template-columns:.8fr 1.2fr;gap:clamp(32px,6vw,96px);align-items:start}
.tb-qas{border-top:1px solid var(--_bd)}
.tb-qa{border-bottom:1px solid var(--_bd)}
.tb-qa summary{list-style:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:24px;padding:26px 0;font-family:var(--_fh);font-size:clamp(18px,1.6vw,22px);line-height:1.3;color:var(--_tx)}
.tb-qa summary::-webkit-details-marker{display:none}
.tb-qa summary i{position:relative;flex:none;width:36px;height:36px;border-radius:50%;border:1px solid var(--_bd);transition:transform .3s,background .3s}
.tb-qa summary i::before,.tb-qa summary i::after{content:"";position:absolute;left:50%;top:50%;width:12px;height:1.5px;background:currentColor;transform:translate(-50%,-50%)}
.tb-qa summary i::after{transform:translate(-50%,-50%) rotate(90deg);transition:opacity .3s}
.tb-qa[open] summary i{background:var(--_ac);color:var(--_on);border-color:var(--_ac)}
.tb-qa[open] summary i::after{opacity:0}
.tb-qa p{margin:0;padding:0 64px 28px 0;font-size:16px;line-height:1.7}
/* cta panel */
.tb-panel{position:relative;overflow:hidden;text-align:center;padding:clamp(56px,8vw,112px) clamp(24px,5vw,80px);border-radius:var(--_rx);background:var(--_ac);color:var(--_on);--_tx:var(--_on);--_mu:color-mix(in srgb,var(--_on) 80%,transparent);display:flex;flex-direction:column;align-items:center}
.tb-panel .tb-glow{inset:auto -20% -60% auto;background:radial-gradient(closest-side,color-mix(in srgb,var(--_on) 22%,transparent),transparent 70%)}
.tb-panel .tb-eyebrow{color:var(--_on);opacity:.85}
.tb-panel .tb-lead{margin-left:auto;margin-right:auto}
.tb-panel .tb-btn{background:var(--_on);color:var(--t-accent,#141414)!important;border-color:var(--_on)}
.tb-panel .tb-btn.ghost{background:transparent;color:var(--_on)!important;border-color:color-mix(in srgb,var(--_on) 40%,transparent)}
.tb-panel.soft{background:var(--_alt);color:var(--_tx);--_tx:var(--t-text,#141414);--_mu:var(--t-muted,#666)}
.tb-panel.soft .tb-eyebrow{color:var(--_ac)}
.tb-panel.soft .tb-btn{background:var(--_ac);color:var(--_on)!important;border-color:var(--_ac)}
.tb-panel.dark{background:var(--t-ink,#111);--_tx:var(--t-paper,#fff);color:var(--_tx);--_mu:color-mix(in srgb,var(--_tx) 70%,transparent)}
.tb-panel.dark .tb-btn{background:var(--t-accent,#fff);color:var(--t-on-accent,#111)!important;border-color:var(--t-accent,#fff)}
/* logos / trust / marquee */
.tb-logos{text-align:center}
.tb-logos-t{margin:0 0 28px;font-size:13px;letter-spacing:.16em;text-transform:uppercase}
.tb-logos-row{display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:clamp(24px,5vw,64px)}
.tb-logos-row img{height:30px;width:auto;opacity:.6;filter:grayscale(1)}
.tb-logos-row span{font-family:var(--_fh);font-size:22px;opacity:.55;letter-spacing:-.01em}
.tb-trust{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:24px}
.tb-trust>div{display:grid;grid-template-columns:auto 1fr;gap:4px 16px;align-items:center}
.tb-trust .tb-ic{grid-row:span 2;margin:0}
.tb-trust b{font-size:15px}.tb-trust span{font-size:14px;color:var(--_mu)}
.tb-marquee{padding:22px 0;background:var(--_ac);color:var(--_on)}
.tb-mq-track{display:flex;gap:36px;width:max-content;animation:tb-mq 38s linear infinite;font-family:var(--_fh);font-size:clamp(22px,2.6vw,34px);white-space:nowrap}
.tb-mq-track i{font-style:normal;opacity:.6}
@keyframes tb-mq{to{transform:translateX(-33.333%)}}
/* footer */
.tb-footer{--_secbg:var(--t-ink,#111);--_tx:var(--t-paper,#F6F4F0);border-top:1px solid var(--_bd);--_mu:color-mix(in srgb,var(--_tx) 62%,transparent);--_bd:color-mix(in srgb,var(--_tx) 14%,transparent);padding-top:clamp(64px,8vw,112px);padding-bottom:32px}
.tb-foot-top{display:grid;grid-template-columns:1.2fr 1fr;gap:48px;padding-bottom:clamp(48px,6vw,88px)}
.tb-wordmark{font-family:var(--_fh);font-size:clamp(44px,7vw,104px);line-height:.95;letter-spacing:-.03em}
.tb-foot-brand p{margin:18px 0 0;max-width:380px}
.tb-foot-cols{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:32px}
.tb-fcol{display:grid;gap:10px;align-content:start}
.tb-fcol h4{margin:0 0 6px;font:600 12px/1 var(--_fb);letter-spacing:.18em;text-transform:uppercase;color:var(--t-accent-soft,var(--_mu))}
.tb-fcol a,.tb-fcol span{color:var(--_mu);text-decoration:none;font-size:15px}
.tb-fcol a:hover{color:var(--_tx)}
.tb-foot-bot{display:flex;justify-content:space-between;gap:16px;padding-top:24px;border-top:1px solid var(--_bd);font-size:13px;color:var(--_mu)}
/* responsive */
@media (max-width:980px){
.tb-split,.tb-split.even,.tb-alt-row,.tb-faq,.tb-reviews,.tb-foot-top{grid-template-columns:1fr}
.tb-split.rev>:first-child,.tb-alt-row.rev>:first-child{order:0}
.tb-grid{grid-template-columns:repeat(min(var(--cols,3),2),minmax(0,1fr))}
.tb-steps{grid-template-columns:repeat(2,minmax(0,1fr))}
.tb-stats{grid-template-columns:repeat(2,minmax(0,1fr));row-gap:40px}
.tb-stat:nth-child(odd){border-left:0;padding-left:0}
.tb-float-a{left:12px}.tb-float-b{right:12px}
.tb-gallery{grid-template-columns:repeat(2,minmax(0,1fr))}
}
@media (max-width:760px){
.tb-links,.tb-nav-in>.tb-btn{display:none}
.tb-burger{display:block}
.tb-nav-in{height:64px}
}
@media (max-width:620px){
.tb-grid,.tb-steps{grid-template-columns:1fr}
.tb-split-text .tb-checks{grid-template-columns:1fr}
.tb-actions .tb-btn{flex:1 1 100%}
.tb-qa p{padding-right:0}
.tb-g.big{grid-column:span 2;grid-row:span 1}
}
@media (prefers-reduced-motion:reduce){.tb *{transition:none!important;animation:none!important}}
`
